import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  AssistantBlock,
  CalcResult,
  DamageMatrix,
  LeadPrediction,
  ReplicaCode,
  SpeedTierTable as SpeedTierData,
} from '../types.ts';
import { BuildSuggestionsCard } from './BuildSuggestionsCard.tsx';
import { CitationBadge } from './CitationBadge.tsx';
import { DamageCalcCard } from './DamageCalcCard.tsx';
import { DamageMatrixCard } from './DamageMatrixCard.tsx';
import { EvSpreadCard } from './EvSpreadCard.tsx';
import { LeadPredictionCard } from './LeadPredictionCard.tsx';
import { ReplicaCodeChip } from './ReplicaCodeChip.tsx';
import { SpeciesParserCard } from './SpeciesParserCard.tsx';
import { SpeedTierTable } from './SpeedTierTable.tsx';
import { TeamCard } from './TeamCard.tsx';
import { ToolCallActivity } from './ToolCallActivity.tsx';
import { TournamentPrecedentsCard } from './TournamentPrecedentsCard.tsx';
import { UntrustedContent } from './UntrustedContent.tsx';
import { VerdictCard } from './VerdictCard.tsx';
import { sourceForChannel } from '../util/channel-source.ts';
import { stripUntrustedTags } from '../util/untrusted.ts';
import { VideoMomentsCard, type VideoMomentsCardData } from './VideoMomentsCard.tsx';

// Renders a single AssistantBlock from the conversation state. Card shapes
// are routed by `card` kind to the right component; unknown shapes fall back
// to a JSON dump so a backend change never silently drops content.
export function BlockRenderer({ block }: { block: AssistantBlock }) {
  switch (block.kind) {
    case 'text':
      // The agent is told never to emit `<untrusted source="…">…</untrusted>`
      // envelope tags in its prose (they're a tool-input wrapper, not display
      // content). Strip at render time as defence-in-depth so a single prompt
      // regression doesn't leak the envelope to the user.
      return (
        <div className="md" style={{ color: 'var(--fg-1)', fontSize: 13.5, lineHeight: 1.55 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripUntrustedTags(block.text)}</ReactMarkdown>
        </div>
      );
    case 'tool':
      return (
        <ToolCallActivity
          tool={block.tool}
          args={block.args}
          elapsed={block.elapsed}
          status={block.status}
        />
      );
    case 'card':
      return <CardBlock card={block.card} data={block.data} />;
    case 'choices':
      // Choices interaction not yet wired to a backend round-trip.
      return null;
  }
}

function CardBlock({ card, data }: { card: string; data: unknown }) {
  switch (card) {
    case 'lead-prediction':
      return <LeadPredictionCard data={data as LeadPrediction} />;
    case 'damage-grid':
      return <DamageCalcCard calc={data as CalcResult} />;
    case 'damage-matrix':
      return <DamageMatrixCard data={data as DamageMatrix} />;
    case 'built-team': {
      // validate_team echoes the parsed roster on success. Shape is our
      // server-side TeamMember[] — close enough to PokemonSpec for TeamCard
      // (optional fields, no teraType requirement at runtime). Expand by
      // default so the user can verify items/moves/EVs at a glance.
      const d = data as { team?: any[]; showdown_export?: string; regulation_id?: string };
      const team = d?.team ?? [];
      if (team.length === 0) return null;
      return (
        <TeamCard
          team={team as any}
          title="proposed team"
          density="expanded"
          defaultExpanded
          showdownExport={d?.showdown_export}
          regulationId={d?.regulation_id}
        />
      );
    }
    case 'ev-spread':
      return <EvSpreadCard spread={data as any} />;
    case 'replica-code': {
      const codes = (data as { results?: ReplicaCode[] })?.results ?? [];
      if (codes.length === 0) return null;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {codes.map((c) => (
            <ReplicaCodeChip key={c.code} code={c} />
          ))}
        </div>
      );
    }
    case 'claim': {
      const results = (data as { results?: any[] })?.results ?? [];
      if (results.length === 0) return null;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {results.map((r) => (
            <div
              key={r.claim_id ?? r.quote}
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              <UntrustedContent source={sourceForChannel(r.channel_name)} variant="quote">
                {r.quote}
              </UntrustedContent>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <CitationBadge
                  source={sourceForChannel(r.channel_name)}
                  suffix={r.channel_name}
                  href={r.source_url}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }
    case 'speed-tier': {
      const d = data as Partial<SpeedTierData> | undefined;
      if (!d || !Array.isArray(d.rows) || d.rows.length === 0) return null;
      // When no my_pokemon was specified, default the anchor for SpeedTierTable
      // to the first row so the highlight has something to land on.
      const anchor = d.my_species ?? d.rows[0]?.species ?? '';
      const my_speed = d.my_speed ?? d.rows[0]?.final_speed ?? 0;
      // Spread the full server response — the widget needs my_base_speed /
      // my_ev_spe / my_nature / my_stage / my_scarf / my_tailwind to
      // pre-populate the interactive controls. The old form (only my_species
      // + my_speed + rows) silently dropped every modifier the agent encoded.
      return (
        <SpeedTierTable
          data={{
            ...d,
            my_species: anchor,
            my_speed,
            rows: d.rows,
          }}
        />
      );
    }
    case 'tournament-precedents':
      return <TournamentPrecedentsCard data={data as any} />;
    case 'build-suggestions':
      return <BuildSuggestionsCard data={data as any} />;
    case 'verdict':
      return <VerdictCard data={data as any} />;
    case 'species-parser':
      return <SpeciesParserCard data={data as any} />;
    case 'video-moments':
      return <VideoMomentsCard data={data as VideoMomentsCardData} />;
    case 'team': {
      // semantic_search_teams returns { results: [{ rendered_team, ... }] }.
      // Render each result as a TeamCard with placement + tournament suffix.
      const results = (data as { results?: any[] })?.results ?? [];
      if (results.length === 0) return null;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {results.slice(0, 5).map((r) => (
            <TeamCard
              key={r.team_id ?? r.source_url}
              team={{
                creator: r.rendered_team?.creator ?? r.player_handle ?? 'unknown',
                date: r.rendered_team?.date ?? r.tournament_date,
                members: r.rendered_team?.members ?? [],
              }}
              title={r.tournament_name ?? 'team'}
              source="limitless"
              sourceMeta={
                r.placement
                  ? `#${r.placement}${r.similarity ? ` · sim ${Number(r.similarity).toFixed(2)}` : ''}`
                  : r.similarity
                    ? `sim ${Number(r.similarity).toFixed(2)}`
                    : undefined
              }
              density="compact"
            />
          ))}
        </div>
      );
    }
    default:
      return (
        <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.5 }}>
          unrendered card: {card}
        </div>
      );
  }
}
